import H6 from "@/components/ui/headings/H6";
import PostAvatar from "@/components/ui/PostAvatar";
import PostTitleDescSection from "../../content-title-desc-section";
import ContentCommentSection from "../../content-comments-section";
import { useEffect, useRef, useState } from "react";
import ContentCommentInputSection from "../../content-comment-input-section";
import Iconify from "@/components/ui/Iconify";
import ContentMediaSection from "../content-media-section";
import ContentOrCommentReporter from "@/components/content-modal-comps/content-or-comment-reporter";
import PostDetailUserFollowBtn from "./content-details-user-follow-btn";
import FullScreenImageSlider from "@/components/content-modal-comps/fullscreen-image-slider";
import { useAuthContext } from "@/context/auth/useAuthContext";
import DeleteNotesWarningModal from "@/components/create-notes-manager-comps/delete-notes-warning-modal";
import { enqueueSnackbar } from "notistack";
import { deletePost } from "@/api/posts";
import TooltipUserNameheading from "./content-tooltip-username-heading";
import { useTranslations } from "@/lib/static-intl";
import { getIdentifier } from "@/utils/helpers";
import { getUserProfileInfoById } from "@/api/creator/user-profile";
import { usePathname } from "next/navigation";
import HoverDropdownButton from "@/components/ui/hover-dropdown-button";

const MOBILE_DETAIL_MEDIA_QUERY = "(max-width: 767px)";

const isMobileDetailMediaViewport = () =>
  typeof window !== "undefined" &&
  window.matchMedia(MOBILE_DETAIL_MEDIA_QUERY).matches;

const useShouldRenderMobileDetailMedia = () => {
  const [shouldRenderMobileMedia, setShouldRenderMobileMedia] = useState(
    isMobileDetailMediaViewport
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia(MOBILE_DETAIL_MEDIA_QUERY);
    const updateMobileMedia = () =>
      setShouldRenderMobileMedia(mediaQuery.matches);

    updateMobileMedia();
    mediaQuery.addEventListener("change", updateMobileMedia);

    return () => {
      mediaQuery.removeEventListener("change", updateMobileMedia);
    };
  }, []);

  return shouldRenderMobileMedia;
};

const ContentDetailsSection = ({
  allPosts,
  closePopup,
  setAllPosts,
  selectedPostInModal,
  allUsersForTagging,
  setSelectedPostInModal,
  currentUserFollowing,
  setCurrentUserFollowing,
  completeRefresh,
  isPiP,
  setIsPiP,
  playerRef,
  handleEnablePIP,
  handleDisablePIP
}) => {
  const apiT = useTranslations("api");
  const explorePostDetailsT = useTranslations("explore.explore_post_details");
  const pathname = usePathname();
  const { user, openLoginModal } = useAuthContext();
  const commentSectionRef = useRef(null);
  const [totalComments, setTotalComments] = useState(0);
  const [currentCommentInReply, setCurrentCommentInReply] = useState(null);

  const [isFullScreenModalOpen, setIsFullScreenModalOpen] = useState(false);
  const [isPostDeleteModalOpen, setIsPostDeleteModalOpen] = useState(false);
  const [postDeleteLoader, setPostDeleteLoader] = useState(false);
  const [fullScreenImages, setFullScreenImages] = useState([]);
  const [userProfileInfo, setUserProfileInfo] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isReportingPost, setIsReportingPost] = useState(false);
  const [commentToBeReported, setCommentToBeReported] = useState(null);
  const shouldRenderMobileMedia = useShouldRenderMobileDetailMedia();
  const containerRef = useRef(null);

  const triggerReportModal = (cmt) => {
    console.log({ cmt });

    if (!user) {
      openLoginModal();
      return;
    }
    if (cmt) {
      setCommentToBeReported(cmt);
      setIsReportingPost(false);
      setIsReportModalOpen(true);
    } else {
      setCommentToBeReported(null);
      setIsReportingPost(true);
      setIsReportModalOpen(true);
    }
  };

  const openProfile = (identifier) => {
    if (!identifier) return;
    window.open(identifier, "blank");
  };

  const triggerFullScreenImageSlider = (slides) => {
    if (!user) {
      openLoginModal();
      return;
    }
    if (!slides || slides?.length === 0) return;
    setIsFullScreenModalOpen(true);
    setFullScreenImages(slides);
  };

  const scrollToCommentSection = () => {
    if (!user) {
      openLoginModal();
    }
    if (commentSectionRef.current) {
      commentSectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleDeletePost = async (postId) => {
    if (!user?.userId) {
      openLoginModal();
      return;
    }

    if (!postId) return;
    try {
      setPostDeleteLoader(true);
      await deletePost(postId);
      closePopup();
      completeRefresh();
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        {
          variant: "error",
        }
      );
    } finally {
      setPostDeleteLoader(false);
    }
  };

  const openDeletePostWarningModal = () => {
    if (!user) {
      openLoginModal();
      return;
    }
    setIsPostDeleteModalOpen(true);
  };

  useEffect(() => {
    if (isPiP || !containerRef.current) return;
    let startY = 0;

    const handleTouchStart = (e) => {
      startY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      const { scrollTop } = containerRef.current;
      const currentY = e.touches[0].clientY;

      // Prevent pull-to-refresh only when at the top
      if (scrollTop === 0 && currentY > startY) {
        e.preventDefault();
      }
    };

    const container = containerRef.current;
    container.addEventListener("touchstart", handleTouchStart, { passive: true });
    container.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
    };
  }, [isPiP]);

  const getUserProfile = async (id) => {
    try {
      const response = await getUserProfileInfoById(id);
      setUserProfileInfo(response.profilePicture);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        {
          variant: "error",
        }
      );
    }
  }

  useEffect(() => {
    getUserProfile(selectedPostInModal?.userId)
  }, [])

  return (
    <div className="relative w-screen md:w-full md:min-w-[30rem] max-w-full md:max-w-[30rem] flex flex-col bg-[#fffefa] dark:bg-dark_bg_grey md:border-l md:border-black/[0.06] md:dark:border-white/[0.10]">
      <div
        className={`sticky z-[70] top-0 w-full flex items-center justify-between bg-[#fffefa]/95 px-3 py-3 md:py-4 hover:cursor-pointer border-b border-black/[0.06] backdrop-blur-md dark:bg-brownish_black/95 dark:border-white/[0.10]`}
      >
        <div className="flex items-center gap-1">
          <div
            className={
              "block md:hidden hover:bg-black hover:bg-opacity-20 transition p-2 rounded-full"
            }
            onClick={(e) => {
              e.stopPropagation();
              closePopup();
            }}
          >
            <Iconify icon={"solar:arrow-left-linear"} />
          </div>

          <div className="max-md:hidden">
          <TooltipUserNameheading
            username={pathname === "/notify" ? user.username : selectedPostInModal?.user?.username}
            currentUserFollowing={currentUserFollowing}
            setCurrentUserFollowing={setCurrentUserFollowing}
          >
            <div className="flex items-center gap-1">
              <PostAvatar
                src={pathname === "/notify" ? userProfileInfo : selectedPostInModal?.user?.profilePicture}
                className={"!w-9 !h-9 object-fill"}
                onClick={() =>
                  openProfile(
                    getIdentifier(selectedPostInModal?.user?.username, selectedPostInModal?.user?.id)
                  )
                }
              />
              <H6
                className={"!leading-[18px] !text-base ml-2"}
                onClick={() =>
                  openProfile(
                    getIdentifier(selectedPostInModal?.user?.username, selectedPostInModal?.user?.id)
                  )
                }
              >
                {pathname === "/notify" ? user.name : selectedPostInModal?.user?.name}
              </H6>
            </div>
          </TooltipUserNameheading>
          </div>
        </div>
        {user && user.userId !== selectedPostInModal?.userId && (
          <div className="relative z-[9999] md:hidden">
            <HoverDropdownButton
              dropdownClasses={"right-0 !top-8 !z-[9999]"}
              options={[
                {
                  id: "detail-report",
                  label: explorePostDetailsT("report"),
                  icon: null,
                  link: null,
                  isHidden: false,
                  onClick: triggerReportModal,
                },
              ]}
            >
              <Iconify icon={"mi:options-vertical"} className={"!w-5 !h-5"} />
            </HoverDropdownButton>
          </div>
        )}
        <div className="max-md:hidden">
        <PostDetailUserFollowBtn
          closePopup={closePopup}
          setAllPosts={setAllPosts}
          selectedPostInModal={selectedPostInModal}
          currentUserFollowing={currentUserFollowing}
          setSelectedPostInModal={setSelectedPostInModal}
          setCurrentUserFollowing={setCurrentUserFollowing}
          openDeletePostWarningModal={openDeletePostWarningModal}
        />
        </div>
      </div>
      <div className="h-[calc(100%-180px)] max-md:h-full md:h-full flex flex-col overflow-y-auto overflow-x-hidden" ref={containerRef}>
        {shouldRenderMobileMedia && (
          <ContentMediaSection
            className={
              "block md:hidden w-full aspect-[3/4] max-h-none"
            }
            variant="mobile-detail"
            selectedPostInModal={selectedPostInModal}
            isPiP={isPiP}
            setIsPiP={setIsPiP}
            playerRef={playerRef}
            handleDisablePIP={handleDisablePIP}
            handleEnablePIP={handleEnablePIP}
          />
        )}
        {/* 2026-07-31 검수 2-A: 상단바에는 나가기·더보기만 두고, 작성자와 팔로우는
            콘텐츠 바로 아래로 내렸다(영상 상세와 같은 순서). 데스크톱은 헤더 그대로. */}
        <div className="flex items-center justify-between gap-3 px-5 pt-4 md:hidden">
          <button
            type="button"
            className="flex min-w-0 items-center gap-2"
            onClick={() =>
              openProfile(
                getIdentifier(
                  selectedPostInModal?.user?.username,
                  selectedPostInModal?.user?.id
                )
              )
            }
          >
            <PostAvatar
              src={selectedPostInModal?.user?.profilePicture}
              className={"!w-9 !h-9 shrink-0 object-fill"}
            />
            <span className="min-w-0 truncate text-[15px] font-bold text-black dark:text-white">
              {selectedPostInModal?.user?.name}
            </span>
          </button>
          <PostDetailUserFollowBtn
            closePopup={closePopup}
            setAllPosts={setAllPosts}
            selectedPostInModal={selectedPostInModal}
            currentUserFollowing={currentUserFollowing}
            setSelectedPostInModal={setSelectedPostInModal}
            setCurrentUserFollowing={setCurrentUserFollowing}
            openDeletePostWarningModal={openDeletePostWarningModal}
          />
        </div>
        <PostTitleDescSection
          currentUserFollowing={currentUserFollowing}
          setCurrentUserFollowing={setCurrentUserFollowing}
          selectedPostInModal={selectedPostInModal}
          triggerReportModal={triggerReportModal}
          allUsersForTagging={allUsersForTagging}
        />
        <ContentCommentSection
          openProfile={openProfile}
          ref={commentSectionRef}
          setTotalComments={setTotalComments}
          selectedPostInModal={selectedPostInModal}
          scrollToCommentSection={scrollToCommentSection}
          setCurrentCommentInReply={setCurrentCommentInReply}
          triggerReportModal={triggerReportModal}
          triggerFullScreenImageSlider={triggerFullScreenImageSlider}
          currentUserFollowing={currentUserFollowing}
          setCurrentUserFollowing={setCurrentUserFollowing}
        />
      </div>
      <FullScreenImageSlider
        isOpen={isFullScreenModalOpen}
        setIsOpen={setIsFullScreenModalOpen}
        slides={fullScreenImages}
      />
      <ContentCommentInputSection
        allPosts={allPosts}
        setAllPosts={setAllPosts}
        totalComments={totalComments}
        allUsersForTagging={allUsersForTagging}
        selectedPostInModal={selectedPostInModal}
        currentCommentInReply={currentCommentInReply}
        scrollToCommentSection={scrollToCommentSection}
        setSelectedPostInModal={setSelectedPostInModal}
        setCurrentCommentInReply={setCurrentCommentInReply}
      />
      <ContentOrCommentReporter
        postData={selectedPostInModal}
        isReportingPost={isReportingPost}
        setIsReportingPost={setIsReportingPost}
        isReportModalOpen={isReportModalOpen}
        setIsReportModalOpen={setIsReportModalOpen}
        commentToBeReported={commentToBeReported}
        setCommentToBeReported={setCommentToBeReported}
      />
      <DeleteNotesWarningModal
        loading={postDeleteLoader}
        isOpen={isPostDeleteModalOpen}
        onClose={() => setIsPostDeleteModalOpen(false)}
        onConfirm={() => handleDeletePost(selectedPostInModal?.id)}
      />
    </div>
  );
};

export default ContentDetailsSection;
